/** Accessibility spec, status text ladder. Both callers share this so the wording
 *  for "waiting" and "ready" cannot drift between the two checkout-adjacent steps. */
export function pendingCheckoutStatusText(isSlow: boolean, isVerySlow: boolean): string {
    if (isVerySlow) {
        return (
            'This is taking longer than expected. It is safe to reload the page and press ' +
            'Continue to payment again — you will be returned to the same payment session and no ' +
            'second application will be created.'
        );
    }
    if (isSlow) return 'Still working. Do not refresh this page.';
    return 'Starting the payment journey. This can take up to 30 seconds.';
}

export const LINK_SUPPRESSED_BY_REPLAY_TEXT =
    ' No new payment link was sent; the existing payment session was reopened.';

export function readyCheckoutStatusText(
    agreementRef: string | null,
    linkSuppressedByReplay = false,
): string {
    const base = `Payment checkout is ready.${agreementRef ? ` Agreement ${agreementRef} has been created as a draft.` : ''}`;
    return linkSuppressedByReplay ? `${base}${LINK_SUPPRESSED_BY_REPLAY_TEXT}` : base;
}

export type CheckoutOutcomeAlert = { title: string; detail: string };

/** The alert region's text for a terminal frame event that is not a success.
 *  Each ends by pointing at Retry, which starts a new checkout. */
export function checkoutOutcomeAlertText(outcome: 'failed' | 'expired' | 'cancelled'): CheckoutOutcomeAlert {
    switch (outcome) {
        case 'failed':
            return {
                title: 'The payment could not be completed.',
                detail: 'Use Retry payment to start a new checkout.',
            };
        case 'expired':
            return {
                title: 'The payment session expired before it was completed.',
                detail: 'Use Retry payment to start a new checkout.',
            };
        case 'cancelled':
            return {
                title: 'The payment was cancelled.',
                detail: 'Use Retry payment to start a new checkout.',
            };
    }
}

/** No rung here says "paid" on the frame's word alone: each one reports only
 *  what the server has confirmed so far. */
export function verificationStatusText(
    state: 'verifying' | 'active' | 'timeout' | 'finalisingTimeout' | 'forbidden' | 'unexpected',
    agreementRef: string | null,
): string {
    const ref = agreementRef ? ` Agreement ${agreementRef}` : ' The agreement';
    switch (state) {
        case 'verifying':
            return 'Payment completed. Verifying with MotorPlan Payments…';
        case 'active':
            return `Payment confirmed.${ref} is now active. Finalising the quote…`;
        case 'finalisingTimeout':
            return `Payment confirmed.${ref} is now active. Finalising the quote is taking longer than usual.`;
        case 'timeout':
            return (
                'Payment completed, but we have not received confirmation from MotorPlan Payments yet. ' +
                'This usually takes under a minute.'
            );
        case 'forbidden':
            return 'Payment completed. Confirmation will appear in Agreements shortly.';
        case 'unexpected':
            return 'Payment completed, but the agreement is not in the expected state. Contact support before continuing.';
    }
}
